import { Component, Inject, Input, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { GridComponent, GridDataResult } from '@progress/kendo-angular-grid';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { IKPIConfigData, KPIConfigService } from 'app/modules/settings/services/kpi-config.service';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import { ToastService } from 'app/modules/shared/services/toast.service';

@Component({
    selector: 'app-kpi-version-dialog',
    templateUrl: './kpi-version-dialog.component.html',
    styleUrls: ['./kpi-version-dialog.component.scss'],
})
export class KpiVersionDialogComponent implements OnInit {
    gridData: GridDataResult;
    sortable = {
        mode: 'single',
        allowUnsort: true,
    };

    sort: SortDescriptor[] = [
        {
            field: 'clientKPIDescription',
            dir: 'asc',
        },
    ];

    allResults: boolean = false;
    scrollable = 'scrollable';
    dialogData: IKPIConfigData;
    selectedDate: string;
    sixMonthDates: FurtureDateData[];
    stopDate: string;
    allData: IKPIConfigData[];
    currentKPI: IKPIConfigData;
    previousKPI: IKPIConfigData;
    getFriendlyDate: (date) => string;
    canAddNewVersion: boolean = true;

    @Input() loading: boolean;
    @ViewChild(GridComponent) grid: GridComponent;
    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        public dialogRef: MatDialogRef<KpiVersionDialogComponent>,
        private kpiConfigService: KPIConfigService,
        private toast: ToastService,
        private reusableFunctions: ReusableFunctions
    ) {}

    ngOnInit() {
        let initialLoad = true;
        this.dialogData = this.data.dialogItem;
        this.allData = this.data.dialogData;
        this.getFriendlyDate = this.reusableFunctions.getFriendlyDateYearFirst;
        this.reloadGrid(initialLoad);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes?.data?.previousValue == undefined && changes?.data?.currentValue != undefined) {
            this.reloadGrid();
        }
    }

    getSixMonthsDates() {
        const today = new Date();
        const dates = [];
        for (let i = 0; i <= 5; i++) {
            const date = new Date(today.getFullYear(), today.getMonth() + i, 1)
                .toLocaleDateString()
                .replace(/\//g, '-');
            dates.push({
                date,
                friendlyDate: this.reusableFunctions.getFriendlyDateMonthFirst(date),
            });
        }
        this.sixMonthDates = dates;
        this.selectedDate = this.sixMonthDates[0].date;
    }

    getScenarioProperties(scenarios) {
        scenarios.sort((a, b) => a.clientKPIKey - b.clientKPIKey);
        this.currentKPI = scenarios[scenarios.length - 1];
        this.previousKPI = scenarios[scenarios.length - 2];

        scenarios.forEach((x) => {
            if (x.stopDate === null && scenarios.length > 1) {
                const latestScenarioStartMonth = this.reusableFunctions.getFriendlyDateYearFirst(
                    x.startDate
                );
                const index = this.sixMonthDates
                    .map((e) => e.friendlyDate)
                    .indexOf(latestScenarioStartMonth);
                this.sixMonthDates = this.sixMonthDates.slice(index + 1);
                this.selectedDate = this.sixMonthDates.length ? this.sixMonthDates[0].date : '';
                x.canDelete = true;
                x.canEdit = true;
            } else {
                const today = new Date();
                const firstDayOfNextMonth = new Date(
                    today.getFullYear(),
                    today.getMonth(),
                    0
                ).getTime();
                if (new Date(x.startDate).getTime() >= firstDayOfNextMonth) {
                    x.canEdit = true;
                }
            }
        });

        this.canAddNewVersion = this.selectedDate === '' ? false : true;
    }

    reloadGrid(initialLoad = false) {
        if (initialLoad) {
            this.getSixMonthsDates();
            let relevantScenarios = this.allData.filter(
                (x) =>
                    x.clientKPIDescription === this.dialogData.clientKPIDescription &&
                    x.clientKPIKey !== 0
            );
            this.getScenarioProperties(relevantScenarios);
            this.gridData = {
                data: orderBy(relevantScenarios, this.sort),
                total: relevantScenarios.length,
            };
            initialLoad = false;
        } else {
            this.kpiConfigService.getKPIConfig().subscribe(
                (results) => {
                    if (results && results.length !== 0) {
                        this.allData = results.filter((x) => x.clientKPIKey !== 0);
                        this.getSixMonthsDates();
                        let relevantScenarios = this.allData.filter(
                            (x) => x.clientKPIDescription === this.dialogData.clientKPIDescription
                        );
                        this.getScenarioProperties(relevantScenarios);

                        this.gridData = {
                            data: orderBy(relevantScenarios, this.sort),
                            total: relevantScenarios.length,
                        };
                    }
                },
                (error) => {
                    throw new Error(error);
                }
            );
        }
    }

    sortChange(sort: SortDescriptor[]): void {
        this.sort = sort;
        this.reloadGrid();
    }

    // Range1End == POOR
    // Range2Start == FAIR START
    // Range2End == FAIR END
    // Range3Start == GOOD START
    // Range3End == GOOD END
    // Range4Start == VERY GOOD START
    // Range4End == VERY GOOD END
    // Range5Start == EXCELLENT

    // ENFORCE BOUND CHECKING FOR INPUT FIELDS
    isInvalid(row) {
        row.errors = null;
        let result = false;
        row.errors = [];
        row.range3EndError = false;
        row.range3StartError = false;
        row.range2StartError = false;
        row.range4EndError = false;

        // Base case
        if (
            !row.target ||
            !row.range3Start ||
            !row.range3End ||
            !row.range2Start ||
            !row.range4End
        ) {
            row.errors = ['You must provide values for each field.'];
            return true;
        }

        // START scores must be greater than END scores
        if (row.bestScore == 'Lowest') {
            /*if (row.range2Start < row.range2End || row.range3Start < row.range3End || row.range4Start < row.range4End ) {
                result = true;
                row.errors.push('All start values must be less than their respective end values.')
            }*/

            // GOOD START must be greater than target
            if (row.range3Start <= row.target) {
                result = true;
                row.range3StartError = true;
                row.errors.push(
                    `Enter a start value for GOOD that is greater than the TARGET value of ${row.target}.`
                );
            }

            // GOOD end must be less than target
            if (row.range3End >= row.target) {
                result = true;

                row.range3EndError = true;
                row.errors.push(
                    `Enter an end value for GOOD that is less than the TARGET value of ${row.target}.`
                );
            }

            // FAIR start must be greater than FAIR end
            if (row.range2Start <= row.range2End) {
                result = true;

                row.range2StartError = true;
                row.errors.push(
                    `Enter a start value for FAIR that is greater than the FAIR end value of ${row.range2End}.`
                );
            }

            // VERY GOOD start must be greater than VERY GOOD end
            if (row.range4End >= row.range4Start) {
                result = true;

                row.range4EndError = true;
                row.errors.push(
                    `Enter an end value for VERY GOOD that is less than the VERY GOOD start value of ${row.range4Start}.`
                );
            }

            // START scores must be less than END scores
        } else if (row.bestScore == 'Highest') {
            row.errors = [];

            // GOOD start must be less than target
            if (row.range3Start >= row.target) {
                result = true;
                row.range3StartError = true;
                row.errors.push(
                    `Enter a start value for GOOD that is less than the TARGET value of ${row.target}.`
                );
            }

            // GOOD end must be greater than target
            if (row.range3End <= row.target) {
                result = true;
                row.range3EndError = true;

                row.errors.push(
                    `Enter an end value for GOOD that is greater than the TARGET value of ${row.target}.`
                );
            }

            // FAIR start must be less than FAIR end
            if (row.range2Start >= row.range2End) {
                result = true;

                row.range2StartError = true;
                row.errors.push(
                    `Enter a start value for FAIR that is less than the FAIR end value of ${row.range2End}.`
                );
            }

            // VERY GOOD start must be greater than VERY GOOD end
            if (row.range4End <= row.range4Start) {
                result = true;

                row.range4EndError = true;
                row.errors.push(
                    `Enter an end value for VERY GOOD that is greater than the VERY GOOD start value of ${row.range4Start}.`
                );
            }
        }
        return result;
    }

    //
    // AUTO SET RANGES FOR NON-INPUT BANDS
    //

    // If Highest is best, Starting scores should be higher than the score being compared to, ex. Fair should be higher than Poor.
    // If Lowest is best, Starting scores should be lower than the score being compared to, ex. Very Good should be higher than Excellent.
    rangeChange(row, destination: string, origin: string, error: string, isStart: boolean) {
        const amountToChange =
            row.kpiFormat === 'currency' || row.clientKPIDescription === 'FICO' ? 1 : 0.001;
        try {
            if (!row[error]) {
                if (row.bestScore == 'Highest') {
                    if (isStart) {
                        row[destination] = this.determinePrecision(row[origin] - amountToChange);
                    } else {
                        row[destination] = this.determinePrecision(row[origin] + amountToChange);
                    }
                } else if (row.bestScore == 'Lowest') {
                    if (isStart) {
                        row[destination] = this.determinePrecision(row[origin] + amountToChange);
                    } else {
                        row[destination] = this.determinePrecision(row[origin] - amountToChange);
                    }
                }
            }
        } catch (e) {}
    }

    determinePrecision(rangeNumber) {
        if (rangeNumber.toString().split('.')[1] > 3) {
            return Number(rangeNumber.toFixed(3));
        }
        return Number(rangeNumber);
    }

    flipSelectedDate(date) {
        const ymcArr = date.split('-');
        const month = Number(ymcArr[0]) >= 10 ? ymcArr[0] : `0${ymcArr[0]}`;
        const day = Number(ymcArr[1]) >= 10 ? ymcArr[1] : `0${ymcArr[1]}`;

        return `${ymcArr[2]}-${month}-${day}`;
    }

    getLastDate() {
        const ymcArr = this.selectedDate.split('-');
        const lastDate = new Date(Number(ymcArr[2]), Number(ymcArr[0]), 0).setDate(0);
        const stopDate = new Date(lastDate).toLocaleDateString().replace(/\//g, '-');
        return this.flipSelectedDate(stopDate);
    }

    add() {
        if (this.selectedDate === '') return;
        const selectedDate = this.flipSelectedDate(this.selectedDate);
        const newScenario = JSON.parse(JSON.stringify(this.currentKPI));
        newScenario.startDate = selectedDate;
        newScenario.stopDate = null;
        newScenario.canDelete = true;
        newScenario.canEdit = true;
        this.save(newScenario, true);
    }

    delete() {
        const today = new Date();
        const firstDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1).getTime();
        const previousKPITime = new Date(this.previousKPI.stopDate).getTime();
        const isPreviousKPIPast =
            this.previousKPI.stopDate === null ? false : previousKPITime > firstDayOfCurrentMonth;
        let formData = this.formatDataForSave([this.currentKPI]);
        this.kpiConfigService
            .deleteKPITimeboxing(formData, this.previousKPI.clientKPIKey, isPreviousKPIPast)
            .subscribe(
                (_) => {
                    this.reloadGrid();
                },
                (error) => {
                    throw new Error(error);
                }
            );
    }

    save(dataItem: IKPIConfigData, isNewVersion: boolean = false) {
        const isAllDataValid = this.kpiConfigService.isAllDataValid([dataItem]);
        if (isAllDataValid) {
            let formData = this.formatDataForSave([dataItem], isNewVersion);
            const today = new Date();
            const firstDayOfCurrentMonth = new Date(
                today.getFullYear(),
                today.getMonth(),
                1
            ).getTime();
            const stopDate =
                new Date(this.currentKPI.stopDate).getTime() > firstDayOfCurrentMonth ||
                this.currentKPI.stopDate === null
                    ? this.getLastDate()
                    : this.currentKPI.stopDate;
            this.kpiConfigService
                .saveKPITimeboxing(formData, false, this.currentKPI.clientKPIKey, stopDate, true)
                .subscribe(
                    (_) => {
                        this.reloadGrid();
                    },
                    (error) => {
                        throw new Error(error);
                    }
                );
        }
    }

    saveEdit() {
        const relevantData = this.allData.filter(
            (x) =>
                x.clientKPIDescription === this.dialogData.clientKPIDescription &&
                x.canEdit === true
        );
        const isAllDataValid = this.kpiConfigService.isAllDataValid(relevantData);
        if (isAllDataValid) {
            let formData = this.formatDataForSave(relevantData);
            this.kpiConfigService.saveKPIConfig(formData).subscribe(
                (_) => {
                    this.toast.success('KPI changes have been saved successfully');
                    this.dialogRef.close();
                    this.reloadGrid();
                },
                (error) => {
                    throw new Error(error);
                }
            );
        }
    }

    formatDataForSave(data, newVersion = false): IKPIConfigData[] {
        let result = [];
        data.forEach((x) => {
            result.push({
                ClientKPIKey: x.clientKPIKey,
                KPIMasterKey: x.kpiMasterKey,
                KPIMilestoneStaffingKey: x.kpiMilestoneStaffingKey,
                KPIMilestoneKey: x.kpiMilestoneKey,
                KPIMilestoneBPSKey: x.kpiMilestoneBPSKey,
                KPIMilestoneCustomKey: x.kpiMilestoneCustomKey,
                ClientKPIDescription: x.clientKPIDescription,
                Target: x.target,
                ActiveDate: x.activeDate,
                InActiveDate: x.inActiveDate,
                IsActive: x.isActive,
                isDeleted: x.isDeleted,
                IsWeightedByLA: x.isWeightedByLA,
                NewVersion: newVersion,
                Range1End: x.range1End,
                Range2Start: x.range2Start,
                Range2End: x.range2End,
                Range3Start: x.range3Start,
                Range3End: x.range3End,
                Range4Start: x.range4Start,
                Range4End: x.range4End,
                Range5Start: x.range5Start,
                StartDate: x.startDate,
                StopDate: x.stopDate,
            });
        });
        return result;
    }
}

export interface FurtureDateData {
    date: string;
    friendlyDate: string;
}
